import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import FalconDropzone from "../common/FalconDropzone";
import DownloadFormatDropdown from "../common/DownloadFormatDropdown";
import { Toast } from "../UI/toast/Toast";
import { CurrencyContext } from "../../context/Context";
import {
  applyInitialLoad,
  downloadInitialLoadTemplate,
  previewInitialLoad,
} from "../../helpers/api/condominium";
import { fetchCondominiumBanksAll } from "../../helpers/api/banks";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { getCurrencyName } from "../../helpers/currencyConversion";

const errorMessage = (error, fallback) =>
  error?.data?.error || Object.values(error?.data?.errors || {}).flat()[0] || fallback;

const InitialLoadModal = ({ isOpen, toggle, selectedCondominium, onSuccess, onVoidAll }) => {
  const { baseCurrency } = useContext(CurrencyContext);
  const [file, setFile] = useState([]);
  const [banks, setBanks] = useState([]);
  const [bankId, setBankId] = useState(null);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [done, setDone] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !selectedCondominium) {
      return undefined;
    }

    let isMounted = true;
    fetchCondominiumBanksAll({ condominiumId: selectedCondominium })
      .then((response) => {
        if (!isMounted) return;
        const list = response?.data || [];
        setBanks(list);
        setBankId((current) =>
          current !== null ? current : list[0] ? String(list[0].condominium_banks_id) : ""
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setBanks([]);
        setBankId((current) => (current !== null ? current : ""));
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, selectedCondominium]);

  const reset = () => {
    setFile([]);
    setResult(null);
    setFailure(null);
    setDone(false);
  };

  const close = () => {
    if (isChecking || isSaving) return;
    reset();
    setBankId(null);
    toggle();
  };

  const baseIso = baseCurrency?.iso || "USD";
  const currencyName = getCurrencyName(baseIso).toLowerCase();
  const currencyLabel = `${currencyName.endsWith("r") ? `${currencyName}es` : `${currencyName}s`} (${baseIso})`;

  const money = (value) =>
    `${baseCurrency?.symbol || "$"} ${Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const buildForm = () => {
    const formData = new FormData();
    formData.append("file", file[0].file);
    if (bankId) formData.append("bank_id", bankId);
    return formData;
  };

  const download = async (format) => {
    setIsDownloading(true);
    try {
      const blob = await downloadInitialLoadTemplate(selectedCondominium, format);
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", `carga_inicial.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const check = async () => {
    if (!file.length) {
      Toast.fire({ type: "error", title: "Por favor selecciona un archivo." });
      return;
    }

    setIsChecking(true);
    setResult(null);
    setFailure(null);
    try {
      const response = await previewInitialLoad(selectedCondominium, buildForm());
      setResult(response?.data || null);
    } catch (error) {
      setFailure(errorMessage(error, "No se pudo revisar el archivo."));
    } finally {
      setIsChecking(false);
    }
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const response = await applyInitialLoad(selectedCondominium, buildForm());
      setResult(response?.data || result);
      setDone(true);
      if (onSuccess) onSuccess();
    } catch (error) {
      if (error?.data?.data) {
        setResult(error.data.data);
      }
      setFailure(errorMessage(error, "No se pudo guardar la carga inicial."));
    } finally {
      setIsSaving(false);
    }
  };

  const summary = result?.summary;
  const errors = result?.errors || [];

  const summaryLines = summary
    ? [
        `Propietarios: ${summary.owners.updated} se actualizan y ${summary.owners.unchanged} quedan igual.`,
        summary.aliquots
          ? Math.abs(Number(summary.aliquots.sum) - 100) > 0.01
            ? `Alícuotas: ${summary.aliquots.updated} se actualizan. Atención: suman ${summary.aliquots.sum}% y no 100%.`
            : `Alícuotas: ${summary.aliquots.updated} se actualizan y suman ${summary.aliquots.sum}%.`
          : "Alícuotas: no cambian.",
        `Deuda inicial: ${summary.initial_debt.units} unidad(es) por ${money(summary.initial_debt.total)}. Cambian ${summary.initial_debt.changed}.`,
        summary.monthly_debt.rows || summary.monthly_debt.changed
          ? `Deuda por mes: ${summary.monthly_debt.rows} concepto(s) por ${money(summary.monthly_debt.total)}. Cambian ${summary.monthly_debt.changed} unidad(es) y período(s).`
          : null,
        summary.monthly_debt.new_periods?.length
          ? `Se crean los períodos ${summary.monthly_debt.new_periods.join(", ")}.`
          : null,
        `Saldo a favor: ${summary.credit.units} unidad(es) por ${money(summary.credit.total)}, que entra a ${summary.credit.account}. Cambian ${summary.credit.changed}.`,
      ].filter(Boolean)
    : [];

  return (
    <Modal isOpen={isOpen} toggle={close} size="lg" centered>
      <ModalHeader toggle={close}>Carga inicial</ModalHeader>
      <ModalBody className="fs--1">
        {done ? (
          <Alert color="success" className="mb-3">
            La carga inicial se guardó.
          </Alert>
        ) : (
          <>
            <p className="mb-2">
              Descarga el archivo con lo que el condominio tiene cargado hoy, complétalo y súbelo. Trae una hoja con una
              fila por unidad, con el propietario, la alícuota, la deuda inicial y el saldo a favor, y una hoja opcional
              para cargar la deuda mes a mes.
            </p>
            <ul className="pl-3 mb-2">
              <li>
                <strong>Los encabezados no se pueden cambiar.</strong> No los renombres, no los borres ni agregues
                columnas: si un encabezado no es el de la plantilla, el archivo no se carga. En Excel vienen bloqueados.
              </li>
              <li>
                <strong>{`Los montos de Deuda inicial, Saldo a favor y la deuda por mes van en ${currencyLabel}`}</strong>
                , no en bolívares.
              </li>
              <li>
                Puedes descargarlo y subirlo en Excel, CSV o texto. La hoja de deuda por mes y las instrucciones solo
                vienen en Excel.
              </li>
            </ul>
            <DownloadFormatDropdown
              label={isDownloading ? "Descargando..." : "Descargar archivo de carga inicial"}
              onSelect={download}
              disabled={isDownloading}
              color="falcon-default"
              size="sm"
              className="mb-3"
            />

            <FormGroup>
              <Label for="initial-load-bank">Cuenta donde entra el saldo a favor</Label>
              <Input
                id="initial-load-bank"
                type="select"
                value={bankId ?? ""}
                onChange={(event) => {
                  setBankId(event.target.value);
                  setResult(null);
                }}
              >
                <option value="">Caja del condominio</option>
                {banks.map((bank) => (
                  <option key={bank.condominium_banks_id} value={bank.condominium_banks_id}>
                    {bank.name}
                    {bank.account_number ? ` (${bank.account_number})` : ""}
                  </option>
                ))}
              </Input>
              <p className="text-700 mt-2 mb-0">
                El saldo a favor se suma a la unidad y a esta cuenta. Registra el saldo de apertura de la cuenta sin
                esos saldos a favor, para que no queden contados dos veces.
              </p>
            </FormGroup>

            <FalconDropzone
              files={file}
              onChange={(files) => {
                setFile(files);
                setResult(null);
                setFailure(null);
              }}
              multiple={false}
              preview
              accept=".xlsx, .xls, .csv, .txt"
              placeholder={
                <>
                  <div className="fs-2 mb-2">
                    <FontAwesomeIcon icon={faFileExcel} />
                  </div>
                  <p className="fs--1 mb-0">Arrastra tu archivo aquí o haz clic</p>
                </>
              }
            />

            {onVoidAll && (
              <div className="border-top pt-3 mt-3">
                <p className="mb-2">
                  ¿Ya cargaste la deuda inicial y quedó mal? Si vuelves a subir el archivo corregido, se reemplaza la
                  deuda de cada unidad que venga en él. Si prefieres empezar de cero, anula toda la deuda inicial y
                  vuelve a cargarla.
                </p>
                <Button
                  color="falcon-danger"
                  size="sm"
                  disabled={isChecking || isSaving}
                  onClick={() => {
                    close();
                    onVoidAll();
                  }}
                >
                  Anular toda la deuda inicial
                </Button>
              </div>
            )}
          </>
        )}

        {failure && !errors.length && (
          <Alert color="danger" className="mt-3 mb-0">
            {failure}
          </Alert>
        )}

        {summary && (
          <div className="mt-3">
            <p className="font-weight-semi-bold mb-1">{done ? "Lo que se cargó:" : "Lo que se va a cargar:"}</p>
            <ul className="pl-3 mb-0">
              {summaryLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}

        {errors.length > 0 && (
          <Alert color="danger" className="mt-3 mb-0">
            <p className="font-weight-semi-bold mb-2">
              {`Hay ${errors.length} problema(s) en el archivo, así que no se puede cargar. Corrígelos y vuelve a subirlo.`}
            </p>
            <div style={{ maxHeight: 260, overflowY: "auto" }}>
              {errors.map((error, index) => (
                <div key={index} className="mb-2">
                  <div className="font-weight-semi-bold">
                    {error.sheet
                      ? `${error.sheet} · Fila ${error.row}${error.unit ? ` · ${error.unit}` : ""}`
                      : "Archivo completo"}
                  </div>
                  <ul className="pl-3 mb-0">
                    {error.messages.map((message, messageIndex) => (
                      <li key={messageIndex}>{message}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="falcon-default" onClick={close} disabled={isChecking || isSaving}>
          {done ? "Cerrar" : "Volver"}
        </Button>
        {!done && (result?.can_apply ? (
          <Button color="primary" onClick={save} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : "Confirmar carga"}
          </Button>
        ) : (
          <Button color="primary" onClick={check} disabled={isChecking || !file.length}>
            {isChecking ? <Spinner size="sm" /> : "Revisar archivo"}
          </Button>
        ))}
      </ModalFooter>
    </Modal>
  );
};

InitialLoadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedCondominium: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSuccess: PropTypes.func,
  onVoidAll: PropTypes.func,
};

export default InitialLoadModal;
